"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";
import Link from "next/link";

const STORAGE_KEY = "fg-wc26-banner-v2";
export const BANNER_HEIGHT = 40; // px — keep in sync with h-10

type BannerCtx = { visible: boolean; dismiss: () => void };
const BannerContext = createContext<BannerCtx>({
  visible: false,
  dismiss: () => {},
});

export function useBanner() {
  return useContext(BannerContext);
}

export function BannerProvider({ children }: PropsWithChildren) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  }, []);

  return (
    <BannerContext.Provider value={{ visible, dismiss }}>
      {children}
    </BannerContext.Provider>
  );
}

export function WorldCupBanner() {
  const { visible, dismiss } = useBanner();

  if (!visible) return null;

  return (
    <div
      role="banner"
      aria-label="World Cup 2026 results announcement"
      className="fixed left-0 right-0 top-0 z-[60] flex h-10 items-center justify-center bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-500 px-4 text-white sm:px-10"
    >
      <Link
        href="/world-cup-2026"
        className="flex items-center gap-2 text-sm transition-opacity hover:opacity-90 sm:gap-2.5"
      >
        <span className="hidden sm:inline">
          Our AI called the{" "}
          <span className="font-semibold">World Cup 2026</span> — 73% hit rate
          on best picks
        </span>
        <span className="font-semibold sm:hidden">
          World Cup 2026 AI results
        </span>
        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-emerald-700 shadow-sm sm:px-3">
          <span className="sm:hidden">See results →</span>
          <span className="hidden sm:inline">See the results →</span>
        </span>
      </Link>

      <button
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 opacity-60 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="size-3.5"
          aria-hidden
        >
          <path d="M2.22 2.22a.75.75 0 0 1 1.06 0L8 6.94l4.72-4.72a.75.75 0 1 1 1.06 1.06L9.06 8l4.72 4.72a.75.75 0 1 1-1.06 1.06L8 9.06l-4.72 4.72a.75.75 0 0 1-1.06-1.06L6.94 8 2.22 3.28a.75.75 0 0 1 0-1.06Z" />
        </svg>
      </button>
    </div>
  );
}
